import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  X,
  ShoppingCart,
  PanelLeft,
  PanelRight,
  UserRound,
  LogIn,
  LogOut,
  UserRoundPlus,
  CircleUserRound,
  BaggageClaim,
} from "lucide-react";
import { Link, NavLink, useNavigate } from "react-router-dom";

export default function Header({
  cartItems = [],
  sidebarOpen,
  toggleSidebar,
  isProductPage,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Check authentication status
  useEffect(() => {
    const checkAuthStatus = () => {
      const customerProfile = localStorage.getItem("customerProfile");
      setIsAuthenticated(!!customerProfile);
    };

    // Check on mount
    checkAuthStatus();

    // Listen for storage changes (in case user logs in/out in another tab)
    const handleStorageChange = () => {
      checkAuthStatus();
    };

    window.addEventListener("storage", handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Handle logout
  const handleLogout = () => {
    // Clear all user-related data from localStorage
    localStorage.removeItem("customer");
    localStorage.removeItem("customerProfile");
    localStorage.removeItem("customerEmail");

    // Also clear any stored password
    // We need to check if there's a customerEmail stored to remove the corresponding password
    const storedEmail = localStorage.getItem("customerEmail");
    if (storedEmail) {
      localStorage.removeItem(`pw_${storedEmail}`);
    }

    // Update authentication status
    setIsAuthenticated(false);

    // Close dropdown
    setDropdownOpen(false);

    // Navigate to sign in page
    navigate("/SignIn");
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <header className="bg-gray-950 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        {isProductPage && (
          <div className="md:hidden">
            <button
              className="text-white focus:outline-none"
              onClick={toggleSidebar}
            >
              {sidebarOpen ? <PanelLeft size={24} /> : <PanelRight size={24} />}
            </button>
          </div>
        )}
        {/* Logo */}
        <div className="flex items-center">
          <img src="../assets/logo.webp" alt="Logo" className="h-14 w-14" />
        </div>
        {/* Search Bar */}
        {/* <div className="w-full sm:w-auto sm:flex-1 sm:mx-4 mt-2 sm:mt-0">
          <input
            type="text"
            placeholder="Search meals..."
            className="w-full px-3 py-2 text-black rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
        </div> */}
        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-12 text-lg text-white">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `px-4 py-2 rounded-3xl transition-colors duration-300 ${
                isActive
                  ? "bg-[#82b016] text-white"
                  : "hover:bg-[#82b016] hover:text-white"
              }`
            }
          >
            Home
          </NavLink>
          <NavLink
            to="/Ecwid"
            className={({ isActive }) =>
              `px-4 py-2 rounded-3xl transition-colors duration-300 ${
                isActive
                  ? "bg-[#82b016] text-white"
                  : "hover:bg-[#82b016] hover:text-white"
              }`
            }
          >
            Products
          </NavLink>
          <NavLink
            to="/verify.php?id1=abcd1234"
            className={({ isActive }) =>
              `px-4 py-2 rounded-3xl transition-colors duration-300 ${
                isActive
                  ? "bg-[#82b016] text-white"
                  : "hover:bg-[#82b016] hover:text-white"
              }`
            }
          >
            Payment
          </NavLink>
          <NavLink
            to="/cart"
            className="px-4 py-2 relative ml-4 cursor-pointer"
          >
            <ShoppingCart className="text-white" />
            {isAuthenticated && cartItems.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#679006] text-white text-sm px-2 py-0.5 rounded-full">
                {cartItems.reduce((total, item) => total + item.quantity, 0)}
              </span>
            )}
          </NavLink>
          {/* Account Dropdown */}
          <div className="relative mt-2 sm:mt-0" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 px-3 py-2 hover:bg-[#679006] hover:text-white rounded-3xl transition-colors duration-300"
            >
              <UserRound className="text-white" />
              <svg
                className={`w-4 h-4 transition-transform ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-gray-950 text-white text-[16px] z-50">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/YourOrders"
                      className="flex align-center items-center py-2 px-3 hover:bg-[#679006] hover:text-white"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <BaggageClaim className="text-white w-4 h-4 mr-2" />
                      My Orders
                    </Link>
                    <Link
                      to="/MyProfile"
                      className="flex align-center items-center py-2 px-3 hover:bg-[#679006] hover:text-white"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <CircleUserRound className="text-white w-4 h-4 mr-2" />
                      My Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex align-center items-center w-full text-left py-2 px-3 hover:bg-[#679006] hover:text-white"
                    >
                      <LogOut className="text-white w-4 h-4 mr-2" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/SignIn"
                      className="flex align-center items-center py-2 px-3 hover:bg-[#679006] hover:text-white"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <LogIn className="text-white w-4 h-4 mr-2" />
                      Sign In
                    </Link>
                    <Link
                      to="/SignUp"
                      className="flex align-center items-center py-2 px-3 hover:bg-[#679006] hover:text-white"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <UserRoundPlus className="text-white w-4 h-4 mr-2" />
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </nav>
        {/* Mobile Hamburger */}
        <button
          className="md:hidden text-gray-100 focus:outline-none"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden px-4 pb-4 space-y-3 text-sm font-medium text-gray-100 trasition-all duration-300">
          <Link
            to="/"
            className="block hover:text-blue-600"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            Home
          </Link>
          <Link
            to="/Ecwid"
            className="block hover:text-blue-600"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            Products
          </Link>
          <Link
            to="/verify.php?id1=abcd1234"
            className="block hover:text-blue-600"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            Payment
          </Link>
          <Link
            to="/cart"
            className="block hover:text-blue-600"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            Cart
          </Link>
          <Link
            to="/SignUp"
            className="block hover:text-[#82b016]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            SignUp
          </Link>
          <Link
            to="/SignIn"
            className="block hover:text-[#82b016]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            SignIn
          </Link>
          <Link
            to="/cart"
            className="relative ml-4 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <ShoppingCart className="text-white" />
            {isAuthenticated && cartItems.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs px-1 rounded-full">
                {cartItems.reduce((total, item) => total + item.quantity, 0)}
              </span>
            )}
          </Link>
          {/* Account Dropdown */}
          <div className="relative mt-2 sm:mt-0" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 px-3 py-2 hover:bg-[#679006] hover:text-white rounded-3xl transition-colors duration-300"
            >
              <UserRound className="text-white" />
              <svg
                className={`w-4 h-4 transition-transform ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-gray-950 text-white text-[16px] z-50">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/YourOrders"
                      className="flex align-center items-center py-2 hover:bg-[#679006] hover:text-white"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <BaggageClaim className="text-white w-4 h-4 mr-2" />
                      My Orders
                    </Link>
                    <Link
                      to="/MyProfile"
                      className="flex align-center items-center py-2 hover:bg-[#679006] hover:text-white"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <CircleUserRound className="text-white w-4 h-4 mr-2" />
                      My Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex align-center items-center w-full text-left py-2 hover:bg-[#679006] hover:text-white"
                    >
                      <LogOut className="text-white w-4 h-4 mr-2" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/SignIn"
                      className="flex align-center items-center py-2 hover:bg-[#679006] hover:text-white"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <LogIn className="text-white w-4 h-4 mr-2" />
                      Sign In
                    </Link>
                    <Link
                      to="/SignUp"
                      className="flex align-center items-center py-2 hover:bg-[#679006] hover:text-white"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <UserRoundPlus className="text-white w-4 h-4 mr-2" />
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
